import { cn } from './internal/utils'

const STATUS_COLOR = {
  success: 'var(--success-green)',
  exception: 'var(--error-color)',
  active: 'var(--primary-color)',
  normal: 'var(--primary-color)',
}

const resolveStroke = (strokeColor, status) => {
  if (typeof strokeColor === 'string') return strokeColor
  // antd also accepts a gradient object: { from, to, direction }
  if (strokeColor?.from && strokeColor?.to) {
    return `linear-gradient(to right, ${strokeColor.from}, ${strokeColor.to})`
  }
  return STATUS_COLOR[status] || STATUS_COLOR.normal
}

const defaultFormat = (percent) => `${Math.round(percent)}%`

const Line = ({ percent, strokeWidth = 8, strokeColor, trailColor, status, strokeLinecap }) => (
  <div
    className="ui-progress-inner w-full overflow-hidden"
    style={{
      height: strokeWidth,
      backgroundColor: trailColor || 'var(--gray-300)',
      borderRadius: strokeLinecap === 'square' ? 0 : 999,
    }}
  >
    <div
      className="ui-progress-bg h-full transition-[width] duration-[var(--duration-slow)] ease-[var(--easing-standard)]"
      style={{
        width: `${percent}%`,
        background: resolveStroke(strokeColor, status),
        borderRadius: 'inherit',
      }}
    />
  </div>
)

const Circle = ({ percent, width = 120, strokeWidth = 6, strokeColor, trailColor, status, type }) => {
  const isDashboard = type === 'dashboard'
  const radius = 50 - strokeWidth / 2
  const circumference = 2 * Math.PI * radius
  const visibleArc = isDashboard ? 0.75 : 1
  const dashOffset = circumference * (1 - (percent / 100) * visibleArc)

  return (
    <svg
      viewBox="0 0 100 100"
      className="ui-progress-circle"
      style={{ width, height: width }}
      aria-hidden="true"
    >
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        stroke={trailColor || 'var(--gray-300)'}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - visibleArc)}
        transform={isDashboard ? 'rotate(135 50 50)' : 'rotate(-90 50 50)'}
      />
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        stroke={resolveStroke(strokeColor, status)}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform={isDashboard ? 'rotate(135 50 50)' : 'rotate(-90 50 50)'}
        style={{ transition: 'stroke-dashoffset var(--duration-slow) var(--easing-standard)' }}
      />
    </svg>
  )
}

/**
 * antd Progress: line, circle and dashboard types, with `status`, `strokeColor`
 * (including the gradient object), `showInfo` and `format`.
 */
const Progress = ({
  percent = 0,
  type = 'line',
  status = 'normal',
  showInfo = true,
  format = defaultFormat,
  size,
  strokeWidth,
  strokeColor,
  strokeLinecap,
  trailColor,
  width,
  className,
  style,
  ...rest
}) => {
  const clamped = Math.max(0, Math.min(100, Number(percent) || 0))
  const resolvedStatus = clamped >= 100 && status === 'normal' ? 'success' : status
  const isCircular = type === 'circle' || type === 'dashboard'
  const lineHeight = strokeWidth ?? (size === 'small' ? 6 : 8)

  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn(
        'ui-progress',
        `ui-progress-status-${resolvedStatus}`,
        isCircular ? 'ui-progress-circle relative inline-flex' : 'ui-progress-line flex items-center gap-2',
        className
      )}
      style={style}
      {...rest}
    >
      {isCircular ? (
        <>
          <Circle
            percent={clamped}
            width={width ?? (typeof size === 'number' ? size : 120)}
            strokeWidth={strokeWidth ?? 6}
            strokeColor={strokeColor}
            trailColor={trailColor}
            status={resolvedStatus}
            type={type}
          />
          {showInfo ? (
            <span className="ui-progress-text absolute inset-0 flex items-center justify-center text-sm font-semibold">
              {format(clamped)}
            </span>
          ) : null}
        </>
      ) : (
        <>
          <Line
            percent={clamped}
            strokeWidth={lineHeight}
            strokeColor={strokeColor}
            trailColor={trailColor}
            status={resolvedStatus}
            strokeLinecap={strokeLinecap}
          />
          {showInfo ? (
            <span className="ui-progress-text shrink-0 text-xs text-[var(--text-muted)]">
              {format(clamped)}
            </span>
          ) : null}
        </>
      )}
    </div>
  )
}

export default Progress
